/**
 * Exportación a Excel del conjunto filtrado de contratos + cuadros.
 */
import ExcelJS from "exceljs";
import type { EnrichedContract } from "./types";

export async function exportContractsToExcel(
  contracts: EnrichedContract[],
  kind: "leasing" | "loan",
  filename: string,
) {
  const wb = new ExcelJS.Workbook();
  const summary = wb.addWorksheet("Resumen contratos");
  const cuadros = wb.addWorksheet("Cuadros");

  const isLoan = kind === "loan";

  summary.columns = [
    { header: "Cliente", key: "company", width: 32 },
    { header: isLoan ? "Entidad" : "Proveedor", key: "supplier", width: 30 },
    { header: "CIF", key: "tax_id", width: 14 },
    { header: "Nº Contrato", key: "contract_number", width: 20 },
    { header: isLoan ? "Descripción" : "Matrícula", key: "plate", width: 16 },
    { header: "Descripción", key: "description", width: 30 },
    { header: "Nº cuotas", key: "total", width: 10 },
    { header: "Pagadas", key: "paid", width: 10 },
    { header: "Pendientes", key: "pending", width: 12 },
    { header: "Capital inicial", key: "initial", width: 16 },
    { header: "Capital pendiente", key: "pendingCap", width: 18 },
    { header: "Intereses pendientes", key: "pendingInt", width: 20 },
    { header: "Cuota media", key: "avg", width: 14 },
    { header: "Próx. vto.", key: "nextDate", width: 12 },
    { header: "Próx. importe", key: "nextAmount", width: 14 },
    { header: "Estado", key: "status", width: 14 },
  ];

  contracts.forEach((c) => {
    summary.addRow({
      company: c.company_name,
      supplier: isLoan ? c.raw.bank_name : c.raw.supplier_tax_id,
      tax_id: isLoan ? c.raw.bank_tax_id : c.raw.supplier_tax_id,
      contract_number: c.raw.contract_number,
      plate: isLoan ? "" : c.raw.plate_number,
      description: c.raw.description || "",
      total: c.metrics.totalInstallments,
      paid: c.metrics.paidInstallments,
      pending: c.metrics.pendingInstallments,
      initial: c.metrics.initialCapital,
      pendingCap: c.metrics.pendingCapital,
      pendingInt: c.metrics.pendingInterest,
      avg: c.metrics.averageInstallment,
      nextDate: c.metrics.nextInstallment?.due_date || "",
      nextAmount: c.metrics.nextInstallment?.total || 0,
      status: c.metrics.status,
    });
  });

  // Totales
  const lastRow = summary.rowCount + 1;
  const totalsRow = summary.addRow({
    company: "TOTALES",
    initial: { formula: `SUM(J2:J${lastRow - 1})` } as any,
    pendingCap: { formula: `SUM(K2:K${lastRow - 1})` } as any,
    pendingInt: { formula: `SUM(L2:L${lastRow - 1})` } as any,
    avg: { formula: `SUM(M2:M${lastRow - 1})` } as any,
    nextAmount: { formula: `SUM(O2:O${lastRow - 1})` } as any,
  });
  totalsRow.font = { bold: true };
  ["J", "K", "L", "M", "O"].forEach((col) => {
    summary.getColumn(col).numFmt = '#,##0.00';
  });
  summary.getRow(1).font = { bold: true };

  // Cuadros hoja
  cuadros.columns = [
    { header: "Cliente", key: "company", width: 30 },
    { header: "Nº Contrato", key: "contract", width: 20 },
    { header: "Cuota", key: "n", width: 8 },
    { header: "Fecha vto.", key: "date", width: 12 },
    { header: isLoan ? "Amort. capital" : "Recup. coste", key: "principal", width: 16 },
    { header: isLoan ? "Intereses" : "Carga financiera", key: "interest", width: 16 },
    { header: "Seguro", key: "insurance", width: 12 },
    { header: "Total", key: "total", width: 14 },
  ];
  contracts.forEach((c) => {
    c.schedule.forEach((s) => {
      cuadros.addRow({
        company: c.company_name,
        contract: c.raw.contract_number,
        n: s.installment_number,
        date: s.due_date,
        principal: s.principal,
        interest: s.interest,
        insurance: s.insurance,
        total: s.total,
      });
    });
  });
  cuadros.getRow(1).font = { bold: true };
  ["E", "F", "G", "H"].forEach((col) => {
    cuadros.getColumn(col).numFmt = '#,##0.00';
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
